import { useState } from "react";
import { Save, Loader2, Check, Users, Bot } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useT } from "../../lib/i18n";

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-emerald-500" : "bg-slate-300"}`}>
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

export default function AgendaSettingsPanel({ settings, onSaved }) {
  const t = useT();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setOk(false); };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.put("/agenda/settings", {
        ai_detection_enabled: form.ai_detection_enabled, mode: form.mode,
        auto_add_threshold: Number(form.auto_add_threshold), default_duration_minutes: Number(form.default_duration_minutes),
        share_with_team: form.share_with_team, team_permission: form.team_permission, timezone: form.timezone,
      });
      setForm(data); setOk(true); onSaved(data);
    } catch (e) { setErr(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="agenda-settings-panel">
      <div className="pm-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center"><Bot size={16} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">AI</div>
            <div className="font-display font-black text-sm">{t("Afspraakherkenning")}</div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-sm"><div className="font-semibold">{t("Herkenning aan")}</div><div className="text-xs text-slate-500">{t("Scan inkomende e-mail/WhatsApp op afspraakverzoeken")}</div></div>
          <Toggle checked={form.ai_detection_enabled} onChange={(v) => set("ai_detection_enabled", v)} testid="agenda-set-ai-enabled" />
        </div>
        <div>
          <div className="text-sm font-semibold mb-2">{t("Werkwijze")}</div>
          <div className="space-y-2">
            {[
              { v: "confirm", l: "Altijd eerst ter bevestiging voorleggen" },
              { v: "auto_high", l: "Automatisch toevoegen bij hoge zekerheid" },
            ].map((o) => (
              <label key={o.v} className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${form.mode === o.v ? "border-emerald-400 bg-emerald-50/40" : "border-slate-200"}`}>
                <input type="radio" name="agenda-mode" checked={form.mode === o.v} onChange={() => set("mode", o.v)} data-testid={`agenda-set-mode-${o.v}`} />
                <span className="text-sm">{t(o.l)}</span>
              </label>
            ))}
          </div>
        </div>
        {form.mode === "auto_high" && (
          <label className="block text-xs font-bold text-slate-600">{t("Drempel voor automatisch toevoegen")} · {Math.round(form.auto_add_threshold * 100)}%
            <input type="range" min="0.6" max="0.99" step="0.01" value={form.auto_add_threshold} onChange={(e) => set("auto_add_threshold", e.target.value)} className="w-full mt-1" data-testid="agenda-set-threshold" />
          </label>
        )}
        <label className="block text-xs font-bold text-slate-600">{t("Standaardduur (minuten)")}
          <input type="number" min="10" max="240" className="pm-input mt-1 w-32" value={form.default_duration_minutes} onChange={(e) => set("default_duration_minutes", e.target.value)} data-testid="agenda-set-duration" />
        </label>
      </div>

      <div className="pm-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center"><Users size={16} /></div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Agency</div>
            <div className="font-display font-black text-sm">{t("Gedeelde agenda")}</div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="text-sm"><div className="font-semibold">{t("Deel mijn agenda met het team")}</div><div className="text-xs text-slate-500">{t("Collega's zien mijn afspraken in het teamoverzicht")}</div></div>
          <Toggle checked={form.share_with_team} onChange={(v) => set("share_with_team", v)} testid="agenda-set-share" />
        </div>
        <div>
          <div className="text-sm font-semibold mb-2">{t("Rechten voor collega's")}</div>
          <div className="space-y-2">
            {[
              { v: "viewer", l: "Alleen inzien" },
              { v: "editor", l: "Ook afspraken toevoegen/bewerken namens mij" },
            ].map((o) => (
              <label key={o.v} className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${form.team_permission === o.v ? "border-blue-400 bg-blue-50/40" : "border-slate-200"}`}>
                <input type="radio" name="agenda-perm" checked={form.team_permission === o.v} onChange={() => set("team_permission", o.v)} disabled={!form.share_with_team} data-testid={`agenda-set-perm-${o.v}`} />
                <span className="text-sm">{t(o.l)}</span>
              </label>
            ))}
          </div>
        </div>
        <label className="block text-xs font-bold text-slate-600">{t("Tijdzone")}
          <input className="pm-input mt-1 w-full" value={form.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="agenda-set-tz" />
        </label>
        {err && <div className="text-xs text-red-600" data-testid="agenda-settings-error">{err}</div>}
        <button onClick={save} disabled={saving} className="pm-btn-primary h-9 px-4 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="agenda-settings-save">
          {saving ? <Loader2 size={12} className="animate-spin" /> : ok ? <Check size={12} /> : <Save size={12} />} {ok ? t("Opgeslagen") : t("Instellingen opslaan")}
        </button>
      </div>
    </div>
  );
}
